import { PAYMENT__DISCOUNT_SHAPE } from '@algoacademy/backend/modules/payment/enums'
import type { MentoringDiscountDraft } from '../../../_components/DiscountControls'
import { GROSZE_PER_ZLOTY } from './constants'
import type { MentoringDiscountSummarySource } from './mentoring-terms'

/**
 * The discount a student already has, as something staff can type over. The
 * length comes from the periods still ahead rather than the length once issued:
 * a change re-issues the discount from the renewal on, so 2 periods left means a
 * 2-period discount - and staff overtypes that whenever it should be different.
 */
function toDiscountDraft(discount: MentoringDiscountSummarySource): MentoringDiscountDraft {
  return {
    shape: discount.shape,
    value:
      discount.shape === PAYMENT__DISCOUNT_SHAPE.AMOUNT
        ? discount.value / GROSZE_PER_ZLOTY
        : discount.value,
    periodCount:
      discount.periodCount === null ? null : (discount.remainingPeriods ?? discount.periodCount),
    // The offer keeps the note staff typed; re-issuing must not silently drop it.
    reason: discount.reason ?? ''
  }
}

type DiscountPrefillArgs = {
  /** The address the lookup ran for, empty until one could belong to somebody. */
  studentEmail: string
  /**
   * What that lookup found: `undefined` while it is still running or was never
   * asked, `null` once it answered that this student has no discount.
   */
  discountInForce: MentoringDiscountSummarySource | null | undefined
  /** The address already prefilled from, so one lookup fills the controls once. */
  prefilledFor: string | null
}

/**
 * Whether a settled lookup has anything to write into the discount controls.
 *
 * Null - leave the draft alone - is the answer almost always, and that is the
 * point: staff prices a discount before typing the e-mail, and a student with no
 * discount to copy must never wipe that work. Only a student who really has one
 * gets copied in, once: the query settles again on every refetch, and a second
 * copy would throw away whatever staff typed in the meantime.
 */
export function readDiscountPrefill(args: DiscountPrefillArgs): MentoringDiscountDraft | null {
  const { studentEmail, discountInForce, prefilledFor } = args

  if (discountInForce === null || discountInForce === undefined) {
    return null
  }

  if (prefilledFor === studentEmail) {
    return null
  }

  return toDiscountDraft(discountInForce)
}
